import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';

// Share a small queue across dashboard and shift avatars. No persistent auth/photo cache.
let active = 0;
const pending = [];
function complete() {active -= 1; drain();}
function drain() {
  while (active < 3 && pending.length) {
    const job = pending.shift();
    if (job.signal.aborted) continue;
    active += 1;
    Promise.resolve().then(job.run).catch(() => {}).finally(complete);
  }
}
export function queuePersonnelPhoto(run, signal) {
  const job = {run, signal};
  pending.push(job);
  const remove = () => {const index = pending.indexOf(job); if (index >= 0) pending.splice(index, 1);};
  signal.addEventListener('abort', remove, {once: true});
  drain();
  return () => {remove(); signal.removeEventListener('abort', remove);};
}

export default function PersonnelPhoto({endpoint, fallbackUrl = '', name, initials, className}) {
  const holder = useRef(null);
  const token = localStorage.getItem('token');
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl, dequeue, started = false;
    setPhoto(null);
    if (!endpoint || !token) return () => controller.abort();
    const start = () => {
      if (started || controller.signal.aborted) return;
      started = true;
      dequeue = queuePersonnelPhoto(async () => {
        if (controller.signal.aborted) return;
        const base = String(process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
        const url = new URL(endpoint, `${base}/`);
        // Never forward the access token to an external photo host.
        if (url.origin !== new URL(base).origin) return;
        const response = await axios.get(url.href, {
          headers: {Authorization: `Bearer ${token}`}, responseType: 'blob',
          signal: controller.signal, timeout: 15000,
        });
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(response.data);
        setPhoto({endpoint, token, url: objectUrl});
      }, controller.signal);
    };
    const observer = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {observer.disconnect(); start();}
        }, {rootMargin: '80px'}) : null;
    if (observer && holder.current) observer.observe(holder.current); else start();
    return () => {observer?.disconnect(); controller.abort(); dequeue?.(); if (objectUrl) URL.revokeObjectURL(objectUrl);};
  }, [endpoint, token]);
  const source = photo?.endpoint === endpoint && photo?.token === token ? photo.url : (!endpoint ? fallbackUrl : '');
  return <span ref={holder} className={className}>
    {source ? <img src={source} alt={name} loading="lazy" decoding="async" /> : initials}
  </span>;
}
